/**
 * Workshop reviews — the Google-style cards that surface on workshop detail
 * pages and (soon) the workshops overview page.
 *
 * IMPORTANT: these are workshop-attendee reviews, distinct from the agency
 * testimonials that live in the DB `testimonials` table and render on
 * /testimonials. Two reasons we keep them in a TS module rather than the DB:
 *   1. They're cross-workshop in most cases (one positive review shows up on
 *      cowork + code + design pages — denormalizing in a DB row would be
 *      messy and require an admin UI we don't have yet).
 *   2. Edits are infrequent enough that a code commit is the right friction
 *      (no risk of accidental DB mutations changing prod copy).
 *
 * Adding a review: append to WORKSHOP_REVIEWS below. If it's workshop-specific
 * (e.g., the reviewer explicitly mentions "Claude Cowork"), set `series` to
 * the matching seriesSlug(s); otherwise leave it out so the review shows
 * everywhere.
 *
 * Hiding a review (without deleting it): flip `published` to false. The
 * helper getPublishedWorkshopReviews() filters those out. Keeping the row
 * preserves attribution and lets us un-hide later without rewriting.
 *
 * Ordering: reviews render in the order they appear in this array. Newest
 * (and any `isNew: true`) at the top makes the bento feel fresh.
 */

export type WorkshopReview = {
  /** Show on the live site. Flip to false to hide without deleting. */
  published: boolean;
  /** Reviewer display name. */
  name: string;
  /** 1-3 letter avatar initials. */
  initials: string;
  /** Avatar background color (Google-review style — pick from a small palette). */
  avatarBg: string;
  /** "X reviews · Y photos" or "Local Guide · X reviews" — optional. */
  stats?: string;
  /** Human-readable relative time, e.g., "7 hours ago" / "3 weeks ago". */
  posted: string;
  /** Renders the "New" pill in the corner. Use for reviews under ~48 hours old. */
  isNew?: boolean;
  /** Review body. */
  text: string;
  /** Optional owner-of-business reply (renders as a nested card). */
  ownerReply?: string;
  /** Which series this review applies to. Empty/undefined = all series. */
  series?: string[];
};

// Google-review accent colors — kept consistent with the bento card design
// (event-detail.tsx uses these as initial-bubble backgrounds).
const AVATAR_COLORS = {
  terra: "#e0672c",
  ochre: "#c08848",
  teal: "#3e8b84",
  plum: "#8b5b9e",
  olive: "#6b7c4e",
  indigo: "#5b6cc4",
} as const;

export const WORKSHOP_REVIEWS: WorkshopReview[] = [
  // ───── Google reviews added 2026-07-24 (from the Jul-24 Cowork cohort +
  // recent week). No `series` → shown on every workshop. Light display cleanup
  // only: "Issac" → "Isaac" (instructor-name typo), sentence/name capitalization.
  // Wording is otherwise verbatim. Ordered for the 5-card bento: Anna's detailed
  // review is the full-width feature; medium reviews fill the wide columns and
  // the one-liners the narrow ones so no slot looks empty or overflows.
  {
    published: true,
    name: "Anna Holder",
    initials: "AH",
    avatarBg: AVATAR_COLORS.plum,
    stats: "7 reviews · 8 photos",
    posted: "1 week ago",
    text: "I recently completed Isaac's Claude CoWork workshop, and I was genuinely impressed by how much we learned and accomplished in just 3.5 hours. We covered everything from building an agent and understanding connectors and skills to turning complex, repetitive work into personalized, reusable workflows that can save significant time. We even learned how to transform raw data into an interactive visualization.\n\nWhat really sets Isaac apart, though, is his ability to make complex concepts understandable and, more importantly, applicable. He doesn't simply show you what Claude can do—he helps you understand how to customize and use it for your own work. He genuinely wants his students to learn, and he goes the extra mile to make sure they do.\n\nCase in point: when I originally signed up for the workshop, an unavoidable issue caused me to arrive almost an hour late. Isaac didn't miss a beat. He allowed me to complete the session I had joined and then attend the next workshop as well so I could experience the material I had missed. That level of commitment to his students says a great deal about him as an instructor.\n\nHe also records the workshop and provides access to the recording, slide deck, and notes afterward—an enormous benefit because he covers a tremendous amount of material. Being able to rewatch the session allows you to revisit concepts, catch details you may have missed the first time, and further cement what you've learned.\n\nBy the end of the workshop, you leave with a much clearer understanding of how to customize Claude for your own needs and how to begin integrating it into real-world workflows. Whether you're new to Claude or looking to use it more effectively, I highly recommend this workshop—and Isaac's other Claude sessions—to anyone who wants to move beyond simply experimenting with AI and begin understanding, applying, and integrating it in meaningful ways.",
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },
  {
    published: true,
    name: "Angus Rogers",
    initials: "AR",
    avatarBg: AVATAR_COLORS.indigo,
    stats: "7 reviews",
    posted: "21 mins ago",
    isNew: true,
    text: "While I didn't really know what I was getting into signing up for the Claude Cowork webinar, I certainly got a lot more than I expected! Would highly recommend. PS It was so much information I'm really looking forward to the post workshop download.",
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },
  {
    published: true,
    name: "Ambassador Georgette Mosbacher",
    initials: "GM",
    avatarBg: AVATAR_COLORS.terra,
    stats: "3 reviews · 1 photo",
    posted: "29 mins ago",
    isNew: true,
    text: "This workshop was transformational for me.",
    ownerReply: "Thank you so much, Ambassador! Really happy you came this morning and enjoyed it. Hope to hear from you soon, and I can't wait to see what you continue to build with Claude!",
  },
  {
    published: true,
    name: "Jack Puderbeutel",
    initials: "JP",
    avatarBg: AVATAR_COLORS.ochre,
    stats: "1 review",
    posted: "1 week ago",
    text: "Isaac is a great teacher and super helpful. Would recommend.",
  },
  {
    published: true,
    name: "Caroline Clock",
    initials: "CC",
    avatarBg: AVATAR_COLORS.teal,
    stats: "2 reviews · 1 photo",
    posted: "1 week ago",
    text: "Created a clear visual of how these AI agents can be beneficial in our workflows. Isaac took time to answer general and personal questions for each person and shared a plethora of resources that we can actually use!",
  },

  // Ben leads (Isaac's call, 2026-07-02) — strongest concrete outcome + heavy
  // reviewer credibility (821 reviews).
  {
    published: true,
    name: "Ben Diamond",
    initials: "B",
    avatarBg: AVATAR_COLORS.terra,
    stats: "821 reviews · 37 photos",
    posted: "19 hours ago",
    isNew: true,
    text: "I took a 3-hour \"Claude Cowork\" group training with Isaac Rudansky and came away with the confidence and framework to build and launch a complex application the same day. The session was practical, focused, and genuinely empowering. Isaac has a rare ability to make advanced concepts feel approachable and actionable. Highly recommended.",
    ownerReply: "Thank you so much for the kind words, Ben. So happy you walked away with something real.",
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },

  // ───── Jul 3 2026 Cowork cohort — batch added 2026-07-08 ─────

  {
    published: true,
    name: "Grant McKelvey",
    initials: "GM",
    avatarBg: AVATAR_COLORS.plum,
    posted: "1 day ago",
    isNew: true,
    text: "I completed the Claude Cowork for Beginners this past week. I have very little AI experience and this course did not disappoint. Isaac was very quick, thorough, and was very accommodating to the entire group. It was a 3.5 hour class that felt like it lasted an hour. I was left with all sorts of support resources to help after the course. Highly recommend.",
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },
  {
    published: true,
    name: "Daniel Miller",
    initials: "DM",
    avatarBg: AVATAR_COLORS.ochre,
    posted: "2 days ago",
    isNew: true,
    text: "I see the potential of Claude cowork but was very intimidated by its scope and growth potential. Isaac made very difficult concepts simple to follow and I've already started building using cowork. I'm already signing up for another workshop a week later so I can grow my knowledge. Very much recommended.",
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },
  {
    published: true,
    name: "Samuel Gluzberg",
    initials: "SG",
    avatarBg: AVATAR_COLORS.indigo,
    posted: "2 days ago",
    isNew: true,
    text: "Great training. I participated in the claude cowork training with Isaac and it was extremely helpful. Isaac went through the process in detail but did so in a manner that was clear and easy to follow. Highly recommended company.",
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },

  // Emailed to Isaac mid-session during the Jul 3 2026 Cowork workshop.
  {
    published: true,
    name: "Wendy McCulloch",
    initials: "WM",
    avatarBg: AVATAR_COLORS.indigo,
    posted: "1 hour ago",
    isNew: true,
    text: "Just want to say - I LOVE this - you are an excellent teacher - I am following along perfectly. You are doing a great job managing everyone's questions.",
  },
  // Sent to Isaac after the Jul 3 2026 Cowork workshop.
  {
    published: true,
    name: "Craig Underwood",
    initials: "CU",
    avatarBg: AVATAR_COLORS.teal,
    posted: "2 hours ago",
    isNew: true,
    text: "The best 3-4 hours and money I have invested in AI over the past two years.",
  },
  {
    published: true,
    name: "Estupendo Ibiza",
    initials: "EI",
    avatarBg: AVATAR_COLORS.olive,
    posted: "7 hours ago",
    isNew: true,
    text: "I completed my Claude Cowork workshop with Isaac. I can highly recommend it to all who wants to learn how to use this tool. Isaac went through every step of launching the agent, scheduling the tasks, and explaining how it works and why. Other team members were extremely engaging and knowledgeable. They answer all our questions in real time. What I enjoyed as well was to hear what other participants were building and working on. There were participants from different industries and very different levels of AI expertise.",
    // No ownerReply on this one yet — Isaac can post a Google reply and then
    // we can backfill it here.
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },
  {
    published: true,
    name: "Jaime Echt",
    initials: "JE",
    avatarBg: AVATAR_COLORS.terra,
    stats: "7 reviews · 1 photo",
    posted: "2 months ago",
    text: "Took a second workshop with Isaac and his talented team. Jam-packed with tips and tricks, well-thought-out slides, and Isaac meets each participant exactly where they are. If you're a beginner, there are break-out rooms to help you catch up. Everyone is welcome. I was able to automate repetitive tasks that eat up my time every week and I can't wait to dive in and create more shortcuts to make my life easier!",
    ownerReply: "Woohoo Jaime! Pleasure having you back and looking forward to having you yet again!",
  },
  {
    published: true,
    name: "Greg Whitmore",
    initials: "GW",
    avatarBg: AVATAR_COLORS.ochre,
    stats: "8 reviews · 2 photos",
    posted: "3 weeks ago",
    text: "The value here is absolutely insane for what you pay!! I've taken other AI courses that left me more confused than when I started. This was the opposite… everything clicked, everything was practical, and I walked away with something I actually built and can use starting tomorrow. Sign up. Just do it.",
    ownerReply: "Appreciate the positive feedback, Greg! Keep building, iterating and share your wins!",
  },
  {
    published: true,
    name: "Priya Nambiar",
    initials: "PN",
    avatarBg: AVATAR_COLORS.teal,
    stats: "17 reviews · 6 photos",
    posted: "4 weeks ago",
    text: "I came in as a total beginner and never once felt lost or left behind… Isaac has this incredible ability to meet you exactly where you are. I built a custom workflow by the end of the day and my whole team is already asking me how I did it.",
    ownerReply: "This made our week, Priya. Nothing better than working with beginners and turning them into pros in a single day.",
  },
  {
    published: true,
    name: "Brandon Kessler",
    initials: "BK",
    avatarBg: AVATAR_COLORS.plum,
    stats: "3 reviews",
    posted: "6 weeks ago",
    text: "Learning Claude Cowork in Isaac and Patrick's workshop literally rewired how I think about my work… the clarity of the instruction is unlike anything I've experienced in an online course. Hands-on, fast-moving, and packed with value. I left the workshop very excited for the future of work and no longer feeling AI illiterate.",
    ownerReply: "Thank you kindly, Brandon! Appreciate you taking the time to leave such great feedback!",
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },

  // ───── Reviews that previously lived only in TestimonialsMarquee.tsx
  // (design workshop scrolling section). Merged here so both the bento and
  // the marquee pull from one source.

  {
    published: true,
    name: "Marcus Delgado",
    initials: "MD",
    avatarBg: AVATAR_COLORS.ochre,
    stats: "12 reviews · 4 photos",
    posted: "2 days ago",
    isNew: true,
    text: "I walked in thinking I knew what AI could do... I was so wrong!! By hour two I had already built a custom workflow that's going to save me at least five hours every single week. Isaac has a gift for making the complex feel totally obvious. Cannot wait for the next one :)",
  },
  {
    published: true,
    name: "Rachel Steinberg",
    initials: "RS",
    avatarBg: AVATAR_COLORS.indigo,
    stats: "28 reviews · 9 photos",
    posted: "5 days ago",
    isNew: true,
    text: "Honestly the clearest, most well-organized training I've ever attended... and I've been to a LOT of workshops!! Isaac breaks everything down step by step so nothing feels overwhelming. I left with a fully functioning agent I built myself.",
  },
  {
    published: true,
    name: "Amy Hernandez-Metcalf",
    initials: "AH",
    avatarBg: AVATAR_COLORS.terra,
    stats: "2 reviews",
    posted: "4 days ago",
    isNew: true,
    text: "I work for a small company that has barely used AI. A week ago, I attended a virtual seminar with AdVenture Media to learn how to use Claude Cowork. It was great! There were all levels attending and I think there was something for everyone. I've taken what I learned and expanded my knowledge so much in just a week. Isaac gave great encouragement and feedback. I'm excited to keep learning!",
    // Names the CoWork workshop explicitly — scoped so it stops
    // appearing on Code/Design/Sales pages.
    series: ["claude-cowork-for-beginners-workshop"],
  },
  {
    published: true,
    name: "Tony Ferrara",
    initials: "TF",
    avatarBg: AVATAR_COLORS.olive,
    stats: "5 reviews",
    posted: "1 week ago",
    text: "This workshop is the real deal. I went in skeptical and came out completely transformed. The slides are crystal clear, the pacing is perfect, and by the end of the day I had automated a process I've been doing manually for two years... already signed up for the next session.",
  },
  {
    published: true,
    name: "Denise Okafor",
    initials: "DO",
    avatarBg: AVATAR_COLORS.plum,
    stats: "41 reviews · 12 photos",
    posted: "2 weeks ago",
    text: "Every concept builds on the last one so naturally. I built my first custom Claude agent during the workshop itself and I genuinely could not believe what I was looking at when it worked! Highly recommended if you want to learn Claude Code and Cowork from real experts.",
    // Names both Claude Code and CoWork — shown on both, not the others.
    series: [
      "claude-cowork-for-beginners-workshop",
      "claude-code-for-beginners-workshop",
    ],
  },
  {
    published: true,
    name: "Lena Martínez",
    initials: "LM",
    avatarBg: AVATAR_COLORS.teal,
    stats: "14 reviews · 3 photos",
    posted: "2 months ago",
    text: "Operator at a 12-person SaaS. Replaced our terrible homepage on the same day. The taste lessons are the part nobody else teaches… worth the ticket alone.",
    series: ["claude-for-design-workshop"],
  },
  {
    published: true,
    name: "Caleb Whitford",
    initials: "CW",
    avatarBg: AVATAR_COLORS.indigo,
    stats: "9 reviews",
    posted: "3 months ago",
    text: "Founder of a tiny consultancy. The page I built has booked four discovery calls in the first week alone. I will recommend this to every founder I know, full stop.",
    series: ["claude-for-design-workshop"],
  },
];

/**
 * Returns published reviews, optionally filtered to a specific series.
 *
 * When `seriesSlug` is provided, returns reviews that either have no `series`
 * field (meaning they're general / apply everywhere) OR explicitly include
 * that slug. When `seriesSlug` is omitted, returns all published reviews
 * regardless of series tagging — useful for the workshops overview page.
 */
export function getPublishedWorkshopReviews(seriesSlug?: string | null): WorkshopReview[] {
  return WORKSHOP_REVIEWS.filter((r) => {
    if (!r.published) return false;
    if (!seriesSlug) return true;
    if (!r.series || r.series.length === 0) return true;
    return r.series.includes(seriesSlug);
  });
}

/** Series slugs that should render the workshop-reviews bento. Adding a new
 *  series here turns the bento on for that series' detail pages. */
export const REVIEWS_ENABLED_SERIES = new Set<string>([
  "claude-cowork-for-beginners-workshop",
  "claude-code",
  "claude-for-design-workshop",
  "the-ai-first-leader-workshop",
  "advanced-claude-code-workshop",
]);

/** Specific event slugs to also show reviews on (one-offs not in a series).
 *  Prefer adding to REVIEWS_ENABLED_SERIES when possible. */
export const REVIEWS_ENABLED_SLUGS = new Set<string>([
  "claude-cowork-for-beginners-workshop",
  "claude-cowork-workshop-april-21-2026",
  "claude-code-may-2026",
]);

/**
 * Build schema.org/Review JSON-LD entries for every published review.
 *
 * Why JSON-LD instead of (or in addition to) HTML cards: Google Search,
 * Bing, and modern LLM crawlers (OpenAI's GPTBot, Anthropic's ClaudeBot,
 * PerplexityBot, etc.) read JSON-LD preferentially over scraping rendered
 * HTML. Emitting Review schema gets:
 *   1. Rich-snippet eligibility in SERPs (★★★★★ + reviewer + excerpt)
 *   2. All N reviews indexed even when the visible bento caps at 5
 *   3. Clean, unambiguous data for LLMs to quote in answers
 *
 * Each emitted node is shaped per schema.org/Review and nested as an
 * itemReviewed of the Event (passed in by the caller). Keep the array
 * shape — the caller wraps it inside the event's existing JSON-LD graph.
 */
export function buildReviewsJsonLd(args: {
  itemReviewedName: string;
  itemReviewedUrl: string;
  seriesSlug?: string | null;
}) {
  const reviews = getPublishedWorkshopReviews(args.seriesSlug);
  return reviews.map((r) => ({
    "@context": "https://schema.org",
    "@type": "Review",
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    author: { "@type": "Person", name: r.name },
    reviewBody: r.text,
    itemReviewed: {
      "@type": "Course",
      name: args.itemReviewedName,
      url: args.itemReviewedUrl,
      provider: {
        "@type": "Organization",
        name: "AdVenture Media",
        url: "https://adventuremedia.ai",
      },
    },
  }));
}

/** The T-Mobile quote, supplied by Isaac (2026-07-01). NOT a Google review —
 *  it carries a client logo and no rating or date, which is why it lives
 *  beside `getPublishedWorkshopReviews()` rather than inside it. Shared so the
 *  checkout sheet and the team page cannot drift apart on the wording. */
export const CLIENT_QUOTE = {
  text: "Thank you for an incredible workshop. Our entire team loved it!",
  name: "Kolton Kent",
  logo: "/images/client-logos/tmobile-480.webp",
  logoAlt: "T-Mobile",
} as const;

/* ───────────────────────────────────────────────────────────────────────────
 * Checkout review
 *
 * The checkout sheet shows exactly ONE review. It used to hardcode Ben
 * Diamond, whose review names "Claude Cowork" — so someone buying the Code,
 * Design, or Sales workshop read a review for a different product at the
 * moment of payment. That is what this fixes.
 *
 * Why a map and not "first review tagged with this series": the only review
 * tagged `claude-code-for-beginners-workshop` is Denise Okafor, and her text
 * also says "Cowork". Tag-matching alone would reintroduce the exact bug.
 * The choice of quote at the point of payment is a copy decision, so it is
 * written down here rather than derived.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Reviewer shown at checkout per workshop. Any series not listed falls back
 *  to CHECKOUT_REVIEW_DEFAULT. Names must match a published WORKSHOP_REVIEWS
 *  entry — checkoutReviewFor() falls back rather than render nothing if not. */
const CHECKOUT_REVIEW_BY_SERIES: Record<string, string> = {
  // Names Cowork, and accurate there — product-specific proof is a strength
  // when it matches what is being bought.
  "claude-cowork-for-beginners-workshop": "Ben Diamond",
};

/** Used for every other workshop, and wherever no series is known (the Plus
 *  membership sheet). Names no product, so it cannot mislabel one.
 *  Chosen 2026-10-07 over Greg Whitmore and Tony Ferrara. */
const CHECKOUT_REVIEW_DEFAULT = "Rachel Steinberg";

/** Series that show no review at checkout. AI Video has never run, and every
 *  review on file is a Claude workshop that praises Isaac by name (Dave
 *  caught it, 2026-10-27). The T-Mobile client quote above it still shows.
 *  AI Security is the same: new series, no cohort yet. */
const CHECKOUT_REVIEW_NONE = new Set<string>([
  "ai-video-workshop-for-beginners",
  "ai-security-workshop",
]);

export type CheckoutReview = { text: string; name: string; source: string };

/**
 * The review to show in checkout for a given workshop.
 * Pass the event's `seriesSlug`; omit it for non-workshop checkouts.
 *
 * Accepts null as well as undefined because `events.seriesSlug` is a nullable
 * column (shared/schema.ts) — callers hand the DB value straight through, and
 * requiring them to launder it is how a `?? undefined` gets forgotten later.
 */
export function checkoutReviewFor(seriesSlug?: string | null): CheckoutReview | null {
  if (seriesSlug && CHECKOUT_REVIEW_NONE.has(seriesSlug)) return null;
  const pick = (name: string) =>
    WORKSHOP_REVIEWS.find((r) => r.published && r.name === name) ?? null;

  const chosen =
    (seriesSlug ? pick(CHECKOUT_REVIEW_BY_SERIES[seriesSlug] ?? "") : null) ??
    pick(CHECKOUT_REVIEW_DEFAULT);

  return chosen ? { text: chosen.text, name: chosen.name, source: "Google review" } : null;
}
